import { faClock, faEye, faEyeSlash, faSquare } from '@fortawesome/free-regular-svg-icons';
import {
  faAlignLeft,
  faArrowAltCircleUp,
  faArrowDown,
  faBell,
  faCalendarDay,
  faCheckCircle,
  faCheckSquare,
  faChevronLeft,
  faChevronRight,
  faClosedCaptioning,
  faCode,
  faExclamationTriangle,
  faEye as faEyeFill,
  faFilm,
  faGraduationCap,
  faHourglassEnd,
  faHourglassStart,
  faICursor,
  faImage,
  faImages,
  faInfoCircle,
  faKey,
  faLock,
  faMusic,
  faPen,
  faSave,
  faScroll,
  faStopwatch,
  faTags,
  faTimes,
  faTools,
  faVideo,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { format } from 'date-fns';
import { ChangeEvent, Dispatch, forwardRef, Fragment, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Dropdown, Form, InputGroup, Modal, ProgressBar, Table } from 'react-bootstrap';
import ReactDiffViewer, { DiffMethod } from 'react-diff-viewer';
import JSONInput from 'react-json-editor-ajrm';
import locale from 'react-json-editor-ajrm/locale/en';
import { useFirebase } from '../../../contexts/FirebaseContext';
import { useApplicationBuildType } from '../../../hooks/useApplicationBuildType';
import { useDetectChanges } from '../../../hooks/useDetectChanges';
import { Copyable } from '../../components/Copyable/Copyable';
import { CountryFlag } from '../../components/CountryFlag/CountryFlag';
import { FullscreenLoader } from '../../components/FullscreenLoader/FullscreenLoader';
import { IconButton } from '../../components/IconButton/IconButton';
import { LanguageCodeInput } from '../../components/LanguageCodeInput/LanguageCodeInput';
import { LanguagesBadge } from '../../components/LanguagesBadge/LanguagesBadge';
import { Placeholder } from '../../components/Placeholder/Placeholder';
import { Plus } from '../../components/Plus/Plus';
import { SubtitleUploader } from '../../components/SubtitleUploader/SubtitleUploader';
import { UserUID } from '../../components/UserUID/UserUID';
import { WithTooltip } from '../../components/WithTooltip/WithTooltip';
import {
  BASIC_FORMATTING_ALLOWED_TAGS,
  DEFAULT_JSON_VIEWER_VALUE,
  formatSecondsTimestamp,
  getLanguageName,
  innerHtmlBasicFormatting,
  stringify,
} from '../../utils/utils';

const AdminVideoComponent = ({ video, setLoading, onUpdate, authors, tags }) => {
  const getProperty = useCallback(
    (property: string) => {
      try {
        return video.i18n.it[property] || (() => ('string' == typeof video[property] ? video[property] : null))();
      } catch {
        return video[property];
      }
    },
    [video]
  );

  const getImage = useCallback(
    (name: string) => {
      try {
        return video[name].it || (() => ('string' == typeof video[name] ? video[name] : null))();
      } catch {
        return video[name];
      }
    },
    [video]
  );

  const { database, videoDataStorage, currentUser, getLogs, timestamp, sendVideoNotifications, fv } = useFirebase();
  const detectChanges = useDetectChanges();
  const { isDevelopment } = useApplicationBuildType();

  const startSeoUpdate = useCallback(() => {
    return database
      .doc(`system/systemData`)
      .set({ seoUpdateInProgress: true, seoUpdateLogs: fv.arrayUnion({ date: Date.now(), id: video.id, message: `Starting SEO update` }) }, { merge: true });
  }, [video]);
  const defaultUpdateValues = useMemo(() => {
    if (!currentUser) return {};
    return { lastEditBy: currentUser.uid, _updateTriggerChange: fv.increment(1) };
  }, [currentUser]);

  const [editLanguage, setEditLanguage] = useState<string>(null);
  const [editLanguageEnabled, setEditLanguageEnabled] = useState(true);
  const [isLanguageCodeValid, setIsLanguageCodeValid] = useState(false);

  const [showRawDataModal, setShowRawDataModal] = useState(false);

  const [showLogsModal, setShowLogsModal] = useState(false);
  const [logsLoading, setLogsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [logs, setLogs] = useState([]);
  const [logDetailsBefore, setLogDetailsBefore] = useState(DEFAULT_JSON_VIEWER_VALUE);
  const [logDetailsAfter, setLogDetailsAfter] = useState(DEFAULT_JSON_VIEWER_VALUE);
  const [selectedLog, setSelectedLog] = useState<any>(null);
  useEffect(() => {
    if (!selectedLog) {
      setLogDetailsBefore(DEFAULT_JSON_VIEWER_VALUE);
      setLogDetailsAfter(DEFAULT_JSON_VIEWER_VALUE);
    }
  }, [selectedLog]);
  useEffect(() => {
    if (!showLogsModal) {
      setCurrentPage(0);
      setLogs([]);
      setSelectedLog(null);
    }
  }, [showLogsModal]);
  useEffect(() => {
    if (!showLogsModal || !video) return;
    setLogsLoading(true);
    getLogs(currentPage * 50, video.id)
      .then(setLogs)
      .finally(() => {
        setLogsLoading(false);
      });
  }, [showLogsModal, currentPage, video]);

  const [genericUploadModal, setGenericUploadModal] = useState(false);
  const [genericUploadProgress, setGenericUploadProgress] = useState(0);
  const [subtitleModalOpen, setSubtitleModalOpen] = useState(false);

  const [showEditTitleModal, setShowEditTitleModal] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newTitleLoading, setNewTitleLoading] = useState(false);
  useEffect(() => {
    if (!showEditTitleModal) setNewTitle('');
  }, [showEditTitleModal]);

  const [showEditThumbnailModal, setShowEditThumbnailModal] = useState(false);
  const [newThumbnail, setNewThumbnail] = useState<File>(null);
  const [thumbnailUploading, setThumbnailUploading] = useState(false);
  const [thumbnailUploadProgress, setThumbnailUploadProgress] = useState(0);
  useEffect(() => {
    if (!showEditThumbnailModal) setNewThumbnail(null);
  }, [showEditThumbnailModal]);

  const [showEditThumbnailVerticalModal, setShowEditThumbnailVerticalModal] = useState(false);
  const [newThumbnailVertical, setNewThumbnailVertical] = useState<File>(null);
  const [thumbnailVerticalUploading, setThumbnailVerticalUploading] = useState(false);
  const [thumbnailVerticalUploadProgress, setThumbnailVerticalUploadProgress] = useState(0);
  useEffect(() => {
    if (!showEditThumbnailVerticalModal) setNewThumbnailVertical(null);
  }, [showEditThumbnailVerticalModal]);

  const [showEditTrailerModal, setShowEditTrailerModal] = useState(false);
  const [newTrailer, setNewTrailer] = useState<File>(null);
  const [trailerUploading, setTrailerUploading] = useState(false);
  const [trailerUploadProgress, setTrailerUploadProgress] = useState(0);
  useEffect(() => {
    if (!showEditTrailerModal) setNewTrailer(null);
  }, [showEditTrailerModal]);

  const [showEditAuthorModal, setShowEditAuthorModal] = useState(false);
  const [newAuthor, setNewAuthor] = useState('');
  const [newAuthorLoading, setNewAuthorLoading] = useState(false);
  useEffect(() => {
    if (!showEditAuthorModal) setNewAuthor('');
  }, [showEditAuthorModal]);

  const [showEditDescriptionModal, setShowEditDescriptionModal] = useState(false);
  const [newDescription, setNewDescription] = useState([]);
  const [newDescriptionLoading, setNewDescriptionLoading] = useState(false);
  useEffect(() => {
    if (!showEditDescriptionModal) setNewDescription([]);
  }, [showEditDescriptionModal]);

  const [showEditInfoModal, setShowEditInfoModal] = useState(false);
  const [newInfo, setNewInfo] = useState('');
  const [newInfoLoading, setNewInfoLoading] = useState(false);
  useEffect(() => {
    if (!showEditInfoModal) setNewInfo('');
  }, [showEditInfoModal]);

  const [showEditCreatedModal, setShowEditCreatedModal] = useState(false);
  const [newCreated, setNewCreated] = useState<any>({});
  const [newCreatedDate, setNewCreatedDate] = useState('');
  const [newCreatedTime, setNewCreatedTime] = useState('');
  const [newCreatedLoading, setNewCreatedLoading] = useState(false);
  useEffect(() => {
    if (!showEditCreatedModal) setNewCreated({});
  }, [showEditCreatedModal]);
  useEffect(() => {
    if (!newCreated || !newCreated._seconds) {
      setNewCreatedDate('');
      setNewCreatedTime('');
    } else {
      setNewCreatedDate(formatSecondsTimestamp(newCreated._seconds, 'y-MM-dd'));
      setNewCreatedTime(formatSecondsTimestamp(newCreated._seconds, 'HH:mm'));
    }
  }, [newCreated]);

  const [showEditTagsModal, setShowEditTagsModal] = useState(false);
  const [newTags, setNewTags] = useState([]);
  const [newTagsLoading, setNewTagsLoading] = useState(false);
  useEffect(() => {
    if (!showEditTagsModal) setNewTags((video.tags || []).map((tag) => tag.id));
  }, [showEditTagsModal]);

  const [showEditKeywordsModal, setShowEditKeywordsModal] = useState(false);
  const [newKeywords, setNewKeywords] = useState('');
  const [newKeywordsLoading, setNewKeywordsLoading] = useState(false);
  useEffect(() => {
    if (!showEditKeywordsModal) setNewKeywords('');
  }, [showEditKeywordsModal]);

  const [showEditTimestampsModal, setShowEditTimestampsModal] = useState(false);
  const [newTimestamps, setNewTimestamps] = useState([]);
  const [newTimestampsLoading, setNewTimestampsLoading] = useState(false);
  useEffect(() => {
    if (!showEditTimestampsModal) setNewTimestamps((video.ts?.slice() || []).map((ts) => ({ ...ts, i18n: { ...(ts.i18n || {}) } })));
  }, [showEditTimestampsModal]);
  const timestampsLanguageMap = Object.entries(
    newTimestamps
      .flatMap((t) => Object.keys(t.i18n || {}))
      .reduce((acc, val) => {
        if (!acc[val]) acc[val] = 0;
        acc[val]++;
        return acc;
      }, {})
  );
  const timestampsLanguageMapDiscrepancy = timestampsLanguageMap.map(([, x]) => x).some((x, _, a) => x != a[0]);

  const [showEditXRayModal, setShowEditXRayModal] = useState(false);
  const [newXRay, setNewXRay] = useState([]);
  const [newXRayLoading, setNewXRayLoading] = useState(false);
  useEffect(() => {
    if (!showEditXRayModal) setNewXRay((video.xray?.slice() || []).map((xray) => ({ ...xray, i18n: { ...(xray.i18n || {}) } })));
  }, [showEditXRayModal]);
  const xrayLanguageMap = Object.entries(
    newXRay
      .flatMap((t) => Object.keys(t.i18n || {}))
      .reduce((acc, val) => {
        if (!acc[val]) acc[val] = 0;
        acc[val]++;
        return acc;
      }, {})
  );
  const xrayLanguageMapDiscrepancy = xrayLanguageMap.map(([, x]) => x).some((x, _, a) => x != a[0]);

  // const [newDate, setNewDate] = useState('');
  // const [newTime, setNewTime] = useState('');
  // const [newCreatedLoading, setNewCreatedLoading] = useState(false);

  const [newIntroSeconds, setNewIntroSeconds] = useState(0);
  const [newOutroSeconds, setNewOutroSeconds] = useState(0);
  const [newIntroOrOutroLoading, setNewIntroOrOutroLoading] = useState(false);

  useEffect(() => {
    if (!video) return;
    setNewTitle(video.title || '');
    setNewDescription(video.description?.slice() || []);
    setNewTags((video.tags || []).map((tag) => tag.id));
    setNewTimestamps((video.ts?.slice() || []).map((ts) => ({ ...ts })));
    setNewXRay((video.xray?.slice() || []).map((xray) => ({ ...xray })));
    // if (video.created) {
    //   setNewDate(formatSecondsTimestamp(video.created._seconds, 'y-MM-dd'));
    //   setNewTime(formatSecondsTimestamp(video.created._seconds, 'HH:mm'));
    // }
    setNewIntroSeconds(video.introSeconds || 19);
    setNewOutroSeconds(video.outroSeconds || 40);
    setNewInfo(video.info || '');
  }, [video]);

  const [showRescheduleModal, setShowRescheduleModal] = useState<boolean>(false);
  const [selectedNotification, setSelectedNotification] = useState<any>();
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [newScheduleLoading, setNewScheduleLoading] = useState<boolean>(false);

  const [showConfirmNotificationDialog, setShowConfirmNotificationDialog] = useState<boolean>(false);

  const handleUploadTask = (guid: string, item: File, itemName: string, progressSetter?: Dispatch<SetStateAction<number>>) => {
    return new Promise<void>((resolve, reject) => {
      const storageRef = videoDataStorage.child(`video_data/${guid}/${itemName}`);
      const uploadTask = storageRef.put(item, { customMetadata: { uid: currentUser.uid } });
      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = ((snapshot.bytesTransferred / snapshot.totalBytes) * 100) | 0;
          if (progressSetter) progressSetter(progress);
        },
        (error) => {
          console.error(error);
          reject(`${error.code}: ${error.message}`);
        },
        () => {
          resolve();
        }
      );
    });
  };

  return (
    <>
      <Card bg={'dark'} data-video-id={video.id}>
        <Card.Header className={'d-flex align-items-center justify-content-between'}>
          <Card.Title className={'mb-0'}>
            {!!video.corsoAso && (
              <Badge variant={'danger'} className={'mr-2'}>
                Corso ASO
              </Badge>
            )}
            {getProperty('title')}
            {!!video.parent && (
              <Badge variant={'info'} className={'ml-2'}>
                PIP
              </Badge>
            )}
          </Card.Title>
          <div>
            <WithTooltip text={video.visible ? 'Clicca per nascondere' : 'Clicca per mostrare'} placement={'left'} big={false}>
              <Button
                as={Badge}
                variant={video.visible ? 'success' : 'secondary'}
                onClick={() => {
                  setLoading(true);
                  Promise.all([
                    database.doc(`videos/${video.id}`).set({ visible: !video.visible, ...defaultUpdateValues }, { merge: true }),
                    startSeoUpdate(),
                  ]).then(onUpdate);
                }}
              >
                <FontAwesomeIcon icon={video.visible ? faEye : faEyeSlash} className={'mr-2'} />
                {video.visible ? 'Visibile' : 'Nascosto'}
              </Button>
            </WithTooltip>
          </div>
        </Card.Header>
        <Card.Body>
          <div className={'d-flex'}>
            <div className={'d-flex flex-column'}>
              <div>{!!getImage('sm_thumbnail') ? <img alt={getProperty('title')} src={getImage('sm_thumbnail')} loading={'lazy'} /> : <Placeholder />}</div>
              {!!video.tags?.length ? (
                video.tags.map((tag) => (
                  <div key={tag.id}>
                    <Badge variant={tag.showAsSeries ? 'warning' : 'info'} className={'mr-1'}>
                      {tag.label}
                    </Badge>
                    <small>
                      <Copyable text={tag.id} />
                    </small>
                  </div>
                ))
              ) : (
                <Badge variant={'secondary'}>Nessun tag</Badge>
              )}
              {!!video.pip && (
                <div>
                  <span className={'mr-2'}>
                    <FontAwesomeIcon icon={faImages} />
                  </span>
                  <small>
                    <code
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        try {
                          document.querySelector(`[data-video-id="${video.pip.id}"]`).scrollIntoView({ behavior: 'smooth' });
                        } catch {}
                      }}
                    >
                      {video.pip.id}
                    </code>
                  </small>
                </div>
              )}
              {!!video.parent && (
                <div>
                  <span className={'mr-2'}>
                    <FontAwesomeIcon icon={faArrowAltCircleUp} />
                  </span>
                  <small>
                    <code
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        try {
                          document.querySelector(`[data-video-id="${video.parent}"]`).scrollIntoView({ behavior: 'smooth' });
                        } catch {}
                      }}
                    >
                      {video.parent}
                    </code>
                  </small>
                </div>
              )}
              <div className={'mt-auto'} />
              <div className={'mt-3 d-flex align-items-center'}>
                <WithTooltip text={'Lingue audio'} big={false}>
                  <FontAwesomeIcon icon={faMusic} className={'mr-1'} fixedWidth={true} />
                </WithTooltip>
                <Badge variant={!!video.languages?.length ? 'primary' : 'danger'} className={'mr-1'}>
                  {video.languages?.length || 0}
                </Badge>
                {!!video.languages?.length && video.languages?.map((lang) => <CountryFlag language={lang} key={lang} withTooltip={true} className={'mr-1'} />)}
              </div>
              <div className={'mt-1 d-flex align-items-center'}>
                <WithTooltip text={'Sottotitoli'} big={false}>
                  <FontAwesomeIcon icon={faClosedCaptioning} className={'mr-1'} fixedWidth={true} />
                </WithTooltip>
                <Badge variant={!!video.subs?.length ? 'primary' : 'secondary'} className={'mr-1'}>
                  {video.subs?.length || 0}
                </Badge>
                {!!video.subs?.length &&
                  video.subs.map((sub) => (
                    <CountryFlag
                      key={sub.languageCode}
                      className={'mr-1'}
                      language={sub.languageCode}
                      withTooltip={true}
                      onClick={() => window.open(sub.url, '_blank')}
                    />
                  ))}
              </div>
              <div className={'mt-1'}>
                <Button as={Badge} variant={'outline-primary'} onClick={() => setSubtitleModalOpen(true)}>
                  <Plus /> Aggiungi/sostituisci sottotitoli
                </Button>
              </div>
              <div className={'mt-3 d-flex flex-column'}>
                <div className={'d-flex align-items-center small'}>
                  <FontAwesomeIcon icon={faBell} className={'mr-1'} />
                  <span>Notifiche</span>
                </div>
                {video.notifications.map((notification) => (
                  <div className={'d-flex align-items-center small'} key={notification.id}>
                    <CountryFlag className={'mr-1'} language={notification.language} withTooltip={true} />
                    <Copyable text={notification.id} />
                    {notification.sent && (
                      <div className={'d-flex align-items-center ml-1'}>
                        <FontAwesomeIcon icon={faCheckCircle} className="mr-1 text-success" />
                        <span>{format(new Date(notification.sentAt._seconds * 1000), 'dd-MM-y HH:mm')}</span>
                      </div>
                    )}
                    {!notification.sent && !!notification.scheduled && (
                      <div className={'d-flex align-items-center ml-1'}>
                        <FontAwesomeIcon icon={faClock} className="mr-1 text-primary" />
                        <span>{format(new Date(notification.scheduled._seconds * 1000), 'dd-MM-y HH:mm')}</span>
                        <Button
                          as={Badge}
                          variant={'outline-primary'}
                          className={'ml-1'}
                          onClick={() => {
                            setShowRescheduleModal(true);
                            setSelectedNotification(notification);
                          }}
                        >
                          Riprogramma
                        </Button>
                      </div>
                    )}
                    {!notification.sent && !notification.scheduled && (
                      <div className={'d-flex align-items-center ml-1'}>
                        <Button
                          as={Badge}
                          variant={'outline-primary'}
                          onClick={() => {
                            setShowRescheduleModal(true);
                            setSelectedNotification(notification);
                          }}
                        >
                          Programma
                        </Button>
                        <Button
                          as={Badge}
                          variant={'primary'}
                          className={'ml-1'}
                          onClick={() => {
                            setShowConfirmNotificationDialog(true);
                            setSelectedNotification(notification);
                          }}
                        >
                          Invia ora
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div className={'flex-grow-1 ml-2'}>
              <div>
                <strong className={'mr-1'}>GUID:</strong>
                <Copyable text={video.id} />
              </div>
              <div>
                <small>
                  <strong className={'mr-1'}>MediaPackage Resource ID:</strong>
                  <Copyable text={video.mediaPackageResourceId} />
                </small>
              </div>
              <div>
                <small>
                  <strong className={'mr-1'}>Filename:</strong>
                  <code>{video.filename}</code>
                </small>
              </div>
              <div className={'d-flex align-items-center my-2 border-left border-secondary pl-1 rounded-left'}>
                <small>
                  <strong className={'mr-1'} style={{ whiteSpace: 'nowrap' }}>
                    Creato il:
                  </strong>
                  <span>{video.created ? formatSecondsTimestamp(video.created._seconds, 'dd/MM/y HH:mm') : 'N/A'}</span>
                  <div className={'text-muted'}>Questa data verrà utilizzata per ogni lingua per cui non viene esplicitamente impostata</div>
                </small>
              </div>
              <div className={'d-flex align-items-center justify-content-start mb-2'}>
                <strong className={'mr-1'} style={{ whiteSpace: 'nowrap' }}>
                  Intro:
                </strong>
                <Form.Control
                  size={'sm'}
                  type={'number'}
                  style={{ maxWidth: '100px' }}
                  value={newIntroSeconds}
                  onChange={(e) => setNewIntroSeconds(+e.target.value)}
                  disabled={newIntroOrOutroLoading}
                />
                <strong className={'ml-2 mr-1'} style={{ whiteSpace: 'nowrap' }}>
                  Outro:
                </strong>
                <Form.Control
                  size={'sm'}
                  type={'number'}
                  style={{ maxWidth: '100px' }}
                  value={newOutroSeconds}
                  onChange={(e) => setNewOutroSeconds(+e.target.value)}
                  disabled={newIntroOrOutroLoading}
                />

                <Button
                  size={'sm'}
                  className={'ml-3'}
                  disabled={!newIntroSeconds || !newOutroSeconds || newIntroOrOutroLoading}
                  onClick={() => {
                    setNewIntroOrOutroLoading(true);
                    Promise.all([
                      database
                        .doc(`videos/${video.id}`)
                        .set({ introSeconds: +newIntroSeconds, outroSeconds: +newOutroSeconds, ...defaultUpdateValues }, { merge: true }),
                      startSeoUpdate(),
                    ])
                      .then(onUpdate)
                      .catch(console.error)
                      .finally(() => setNewIntroOrOutroLoading(false));
                  }}
                >
                  <FontAwesomeIcon icon={faSave} />
                </Button>
              </div>
              <div className={'mb-2'}>
                {!!video.author ? (
                  <div className={'d-flex align-items-center'}>
                    <div className={'mr-2'}>
                      {!!video.author.photo ? (
                        <img src={video.author.photo} alt={video.author.name} style={{ width: '50px', height: '50px', borderRadius: '50%' }} />
                      ) : (
                        <Placeholder width={50} height={50} round />
                      )}
                    </div>
                    <div className={'d-flex flex-column'}>
                      <span>{video.author.name}</span>
                      <small>
                        <Copyable text={video.author.id} />
                      </small>
                    </div>
                  </div>
                ) : (
                  <span className={'text-warning'}>
                    <FontAwesomeIcon icon={faExclamationTriangle} className={'mr-2'} />
                    <span>Nessun autore impostato</span>
                  </span>
                )}
              </div>
              <div className={'mt-1'}>
                <Button
                  as={Badge}
                  variant={video.visibleFor?.odontoiatra ? 'success' : 'secondary'}
                  onClick={() => {
                    setLoading(true);
                    Promise.all([
                      database.doc(`videos/${video.id}`).set(
                        {
                          visibleFor: {
                            odontoiatra: !video.visibleFor?.odontoiatra,
                            aso: !!video.visibleFor?.aso,
                          },
                          ...defaultUpdateValues,
                        },
                        { merge: true }
                      ),
                      startSeoUpdate(),
                    ]).then(onUpdate);
                  }}
                >
                  <FontAwesomeIcon icon={video.visibleFor?.odontoiatra ? faCheckSquare : faSquare} className={'mr-1'} />
                  Odontoiatra
                </Button>
                <Button
                  as={Badge}
                  variant={video.visibleFor?.aso ? 'success' : 'secondary'}
                  className={'ml-2'}
                  onClick={() => {
                    setLoading(true);
                    Promise.all([
                      database.doc(`videos/${video.id}`).set(
                        {
                          visibleFor: {
                            odontoiatra: !!video.visibleFor?.odontoiatra,
                            aso: !video.visibleFor?.aso,
                          },
                          ...defaultUpdateValues,
                        },
                        { merge: true }
                      ),
                      startSeoUpdate(),
                    ]).then(onUpdate);
                  }}
                >
                  <FontAwesomeIcon icon={video.visibleFor?.aso ? faCheckSquare : faSquare} className={'mr-1'} />
                  ASO
                </Button>

                {!video.corsoAso && (
                  <Button
                    as={Badge}
                    variant={video.free ? 'success' : 'secondary'}
                    className={'ml-5'}
                    onClick={() => {
                      setLoading(true);
                      Promise.all([
                        database.doc(`videos/${video.id}`).set(
                          {
                            free: !video.free,
                            ...defaultUpdateValues,
                          },
                          { merge: true }
                        ),
                        startSeoUpdate(),
                      ]).then(onUpdate);
                    }}
                  >
                    <FontAwesomeIcon icon={video.free ? faCheckSquare : faSquare} className={'mr-1'} />
                    VIDEO GRATUITO (accessibile a utenti non abbonati)
                  </Button>
                )}
              </div>
              {/*              <details>
                <summary>
                  Descrizione <Badge variant={!!getProperty('description')?.length ? 'primary' : 'secondary'}>{video.description?.length || 0}</Badge>
                </summary>
                <div className={'border border-info rounded px-2 py-1'}>
                  {!!getProperty('description')?.length
                    ? getProperty('description').map((segment, i) => <div key={i.toString()}>{segment}</div>)
                    : 'Nessuna descrizione'}
                </div>
              </details>*/}
              {/*              <details>
                <summary>
                  Timestamps <Badge variant={!!video.ts?.length ? 'primary' : 'secondary'}>{video.ts?.length || 0}</Badge>
                </summary>
                <div className={'border border-info rounded px-2 py-1'}>
                  {!!video.ts?.length
                    ? video.ts.map((ts, i) => (
                        <div key={i.toString()}>
                          ({ts.at}) {ts.description}
                        </div>
                      ))
                    : 'Nessun timestamp'}
                </div>
              </details>*/}
              {/*              <details>
                <summary>
                  Strumentazione <Badge variant={!!video.xray?.length ? 'primary' : 'secondary'}>{video.xray?.length || 0}</Badge>
                </summary>
                <div className={'border border-info rounded px-2 py-1'}>
                  {!!video.xray?.length
                    ? video.xray.map((xray, i) => (
                        <div key={i.toString()}>
                          ({xray.from}-{xray.to}) {xray.description}
                        </div>
                      ))
                    : 'Nessuna strumentazione'}
                </div>
              </details>*/}
              {/*              <details>
                <summary>
                  Keywords <Badge variant={!!video.keywords ? 'primary' : 'secondary'}>{video.keywords?.split?.(',')?.length || 0}</Badge>
                </summary>
                <div className={'border border-info rounded px-2 py-1'}>{video.keywords || 'Nessuna keyword.'}</div>
              </details>*/}
            </div>
          </div>
        </Card.Body>
        <Card.Footer>
          <div className={'d-flex align-items-center'}>
            <Button variant={'dark'} disabled size={'sm'} style={{ width: '115px' }}>
              <div className={'d-flex justify-content-start align-items-center'}>
                <FontAwesomeIcon icon={faEyeFill} fixedWidth={true} className={'mr-1'} />
                <span>Visualizza:</span>
              </div>
            </Button>
            {[
              ['Thumbnail', faImage, video.thumbnail, null],
              ['Thumbnail (sm)', faImage, video.sm_thumbnail, null, { size: 'xs' }],
              ['Thumbnail verticale', faImage, video.thumbnail_vertical, null, { rotation: 270 }],
              ['Thumbnail verticale (sm)', faImage, video.sm_thumbnail_vertical, null, { rotation: 270, size: 'xs' }],
              ['Trailer', faFilm, video.trailer, null],
              ['Video', faVideo, video.hls, `../watch/${video.id}`],
            ].map(([label, icon, property, path, iconProps = {}], i) => {
              if (path) {
                return (
                  <WithTooltip text={label} big={false} key={i.toString()}>
                    <Button
                      size={'sm'}
                      className={'ml-1'}
                      variant={property ? 'outline-primary' : 'danger'}
                      disabled={!property}
                      onClick={() => window.open(path, '_blank')}
                    >
                      <FontAwesomeIcon icon={icon} fixedWidth={true} {...iconProps} />
                    </Button>
                  </WithTooltip>
                );
              }

              const valuesAmount = Object.keys(property || {}).length;
              const hasValues = !!valuesAmount;
              return (
                <Dropdown key={i.toString()}>
                  <Dropdown.Toggle
                    as={forwardRef(({ onClick }: any, _) => (
                      <WithTooltip text={label} big={false}>
                        <Button
                          size={'sm'}
                          className={'ml-1'}
                          variant={hasValues ? 'outline-primary' : 'danger'}
                          disabled={!hasValues}
                          onClick={(e) => {
                            e.preventDefault();
                            onClick(e);
                          }}
                        >
                          <FontAwesomeIcon icon={icon} fixedWidth={true} {...iconProps} />
                          <LanguagesBadge className={'ml-1'} amount={valuesAmount} />
                        </Button>
                      </WithTooltip>
                    ))}
                  />

                  {hasValues && (
                    <Dropdown.Menu>
                      {Object.entries<string>(property).map(([language, url]) => (
                        <Dropdown.Item key={language} onClick={() => window.open(url, '_blank')}>
                          <CountryFlag language={language} />
                          <span className={'ml-1'}>{getLanguageName(language)}</span>
                        </Dropdown.Item>
                      ))}
                    </Dropdown.Menu>
                  )}
                </Dropdown>
              );
            })}
            <WithTooltip text={'Raw data'} big={false}>
              <Button size={'sm'} className={'ml-1'} variant={'outline-primary'} onClick={() => setShowRawDataModal(true)}>
                <FontAwesomeIcon icon={faCode} fixedWidth={true} />
              </Button>
            </WithTooltip>
            <WithTooltip text={'Logs'} big={false}>
              <Button size={'sm'} className={'ml-1'} variant={'outline-primary'} onClick={() => setShowLogsModal(true)}>
                <FontAwesomeIcon icon={faScroll} fixedWidth={true} />
              </Button>
            </WithTooltip>
          </div>
        </Card.Footer>
        <Card.Footer>
          <div className={'d-flex align-items-center'}>
            <Button variant={'dark'} disabled size={'sm'} style={{ width: '115px' }}>
              <div className={'d-flex justify-content-start align-items-center'}>
                <FontAwesomeIcon icon={faPen} fixedWidth={true} className={'mr-1'} />
                <span>Modifica:</span>
              </div>
            </Button>
            {[
              ['Titolo', faICursor, video.i18n, 'title', '', setNewTitle, setShowEditTitleModal],
              ['Thumbnail', faImage, video.thumbnail, null, null, setNewThumbnail, setShowEditThumbnailModal],
              // prettier-ignore
              ['Thumbnail verticale', faImage, video.thumbnail_vertical, null, null, setNewThumbnailVertical, setShowEditThumbnailVerticalModal, { rotation: 270 }],
              ['Trailer', faFilm, video.trailer, null, null, setNewTrailer, setShowEditTrailerModal],
              ['Descrizione', faAlignLeft, video.i18n, 'description', [], setNewDescription, setShowEditDescriptionModal],
              ['Data creazione', faCalendarDay, video.i18n, 'created', {}, setNewCreated, setShowEditCreatedModal],
              ['Autore', faGraduationCap, null, null, null, null, setShowEditAuthorModal],
              ['Tags', faTags, null, null, null, null, setShowEditTagsModal],
              // ['Keywords', faKey, video.i18n, 'keywords', '', setNewKeywords, setShowEditKeywordsModal],
              ['Timestamps', faStopwatch, null, null, null, null, setShowEditTimestampsModal],
              ['Strumentazione', faTools, null, null, null, null, setShowEditXRayModal],
              ['Informazioni', faInfoCircle, video.i18n, 'info', [], setNewInfo, setShowEditInfoModal],
            ].map(([label, icon, path, property, defaultValue, propertySetter, modalSetter, iconProps = {}]: any, i) => {
              if (!propertySetter) {
                return (
                  <WithTooltip key={i.toString()} text={label} big={false}>
                    <Button variant={'outline-primary'} size={'sm'} className={'ml-1'} onClick={() => modalSetter(true)}>
                      <FontAwesomeIcon icon={icon} fixedWidth={true} {...iconProps} />
                    </Button>
                  </WithTooltip>
                );
              }

              const values = Object.values(path || {})
                .map((o) => (property ? o[property] : o))
                .filter(Boolean);
              const valuesAmount = values.length;

              return (
                <Dropdown key={i.toString()}>
                  <Dropdown.Toggle
                    as={forwardRef(({ onClick }: any, _) => (
                      <WithTooltip text={label} big={false}>
                        <Button
                          size={'sm'}
                          className={'ml-1'}
                          variant={'outline-primary'}
                          onClick={(e) => {
                            e.preventDefault();
                            onClick(e);
                          }}
                        >
                          <FontAwesomeIcon icon={icon} fixedWidth={true} {...iconProps} />
                          <LanguagesBadge className={'ml-1'} amount={valuesAmount} />
                        </Button>
                      </WithTooltip>
                    ))}
                  />

                  <Dropdown.Menu>
                    {Object.entries<any>(path || {})
                      .map(([k, v]) => [k, property ? v[property] : v])
                      .filter(([, v]) => !!v)
                      .map(([language, value]) => {
                        return (
                          <Dropdown.Item
                            key={language}
                            onClick={() => {
                              setEditLanguage(language);
                              setEditLanguageEnabled(false);
                              propertySetter(value);
                              modalSetter(true);
                            }}
                          >
                            <CountryFlag language={language} withText={true} />
                          </Dropdown.Item>
                        );
                      })}
                    <Dropdown.Item
                      className={'text-primary'}
                      onClick={() => {
                        setIsLanguageCodeValid(false);
                        setEditLanguage('');
                        setEditLanguageEnabled(true);
                        propertySetter(defaultValue);
                        modalSetter(true);
                      }}
                    >
                      <Plus /> Aggiungi lingua
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              );
            })}

            {(() => {
              const values = Object.entries<string>(video)
                .filter(([key]) => key.startsWith('keywords'))
                .map(([key, value]) => {
                  let [, lang] = key.split('_');
                  if (!lang) lang = 'it';
                  return [lang, value];
                });
              const valuesAmount = values.length;

              return (
                <Dropdown>
                  <Dropdown.Toggle
                    as={forwardRef(({ onClick }: any, _) => (
                      <WithTooltip text={'Keywords'} big={false}>
                        <Button
                          size={'sm'}
                          className={'ml-1'}
                          variant={'outline-primary'}
                          onClick={(e) => {
                            e.preventDefault();
                            onClick(e);
                          }}
                        >
                          <FontAwesomeIcon icon={faKey} fixedWidth={true} />
                          <LanguagesBadge className={'ml-1'} amount={valuesAmount} />
                        </Button>
                      </WithTooltip>
                    ))}
                  />

                  <Dropdown.Menu>
                    {values.map(([language, value]) => {
                      return (
                        <Dropdown.Item
                          key={language}
                          onClick={() => {
                            setEditLanguage(language);
                            setEditLanguageEnabled(false);
                            setNewKeywords(value);
                            setShowEditKeywordsModal(true);
                          }}
                        >
                          <CountryFlag language={language} withText={true} />
                        </Dropdown.Item>
                      );
                    })}
                    <Dropdown.Item
                      className={'text-primary'}
                      onClick={() => {
                        setIsLanguageCodeValid(false);
                        setEditLanguage('');
                        setEditLanguageEnabled(true);
                        setNewKeywords('');
                        setShowEditKeywordsModal(true);
                      }}
                    >
                      <Plus /> Aggiungi lingua
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              );
            })()}
          </div>
        </Card.Footer>
      </Card>

      {showEditTitleModal && (
        <Modal show={showEditTitleModal} backdrop={'static'} keyboard={false} onHide={() => setShowEditTitleModal(false)}>
          <Modal.Header>
            <Modal.Title>Modifica titolo</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <LanguageCodeInput
              value={editLanguage}
              onChange={(e) => setEditLanguage(e)}
              disabled={!editLanguageEnabled || newTitleLoading}
              onErrorChange={(e) => setIsLanguageCodeValid(!e)}
              autoFocus={editLanguageEnabled}
            />

            <Form.Group>
              <Form.Label>Titolo</Form.Label>
              <Form.Control
                autoFocus
                disabled={newTitleLoading}
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder={'Inserisci un titolo...'}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant={'secondary'} disabled={newTitleLoading} onClick={() => setShowEditTitleModal(false)}>
              Annulla
            </Button>
            <Button
              variant={'primary'}
              disabled={!newTitle || newTitleLoading || !isLanguageCodeValid}
              onClick={() => {
                setNewTitleLoading(true);
                Promise.all([
                  database.doc(`videos/${video.id}`).set(
                    {
                      i18n: {
                        [editLanguage]: {
                          title: newTitle,
                        },
                      },
                      ...defaultUpdateValues,
                    },
                    { merge: true }
                  ),
                  startSeoUpdate(),
                ])
                  .then(() => {
                    setShowEditTitleModal(false);
                    onUpdate();
                  })
                  .catch(console.error)
                  .finally(() => setNewTitleLoading(false));
              }}
            >
              Conferma
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      {showEditCreatedModal && (
        <Modal show={showEditCreatedModal} backdrop={'static'} keyboard={false} onHide={() => setShowEditCreatedModal(false)}>
          <Modal.Header>
            <Modal.Title>Modifica data creazione</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Alert variant={'danger'}>Modificare questa impostazione causerà la riprogrammazione della relativa notifica.</Alert>
            <LanguageCodeInput
              value={editLanguage}
              onChange={(e) => setEditLanguage(e)}
              disabled={!editLanguageEnabled || newCreatedLoading}
              onErrorChange={(e) => setIsLanguageCodeValid(!e)}
              autoFocus={editLanguageEnabled}
            />

            <Form.Group>
              <Form.Label>Data creazione</Form.Label>
              <InputGroup>
                <Form.Control
                  type={'date'}
                  disabled={newCreatedLoading}
                  style={{ maxWidth: '155px' }}
                  value={newCreatedDate}
                  onChange={(e) => setNewCreatedDate(e.target.value)}
                />
                <Form.Control
                  type={'time'}
                  disabled={newCreatedLoading}
                  style={{ maxWidth: '125px' }}
                  value={newCreatedTime}
                  onChange={(e) => setNewCreatedTime(e.target.value)}
                />
              </InputGroup>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant={'secondary'} disabled={newCreatedLoading} onClick={() => setShowEditCreatedModal(false)}>
              Annulla
            </Button>
            <Button
              variant={'primary'}
              disabled={!newCreatedDate || newCreatedLoading || !isLanguageCodeValid}
              onClick={() => {
                setNewCreatedLoading(true);
                Promise.all([
                  database
                    .doc(`videos/${video.id}`)
                    .set(
                      {
                        i18n: {
                          [editLanguage]: {
                            created: timestamp.fromDate(new Date(`${newCreatedDate}T${newCreatedTime}:00`)),
                          },
                        },
                        ...defaultUpdateValues,
                      },
                      { merge: true }
                    )
                    .then(() => {
                      const notification = video.notifications.find((x) => x.language == editLanguage && !x.sent);
                      if (!notification) return;
                      return database
                        .doc(`videoNotifications/${notification.id}`)
                        .set({ scheduled: timestamp.fromDate(new Date(`${newCreatedDate}T${newCreatedTime}:00`)) }, { merge: true });
                    }),
                  startSeoUpdate(),
                ])
                  .then(() => {
                    setShowEditCreatedModal(false);
                    onUpdate();
                  })
                  .catch(console.error)
                  .finally(() => setNewCreatedLoading(false));
              }}
            >
              Conferma
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      {showEditThumbnailModal && (
        <Modal show={showEditThumbnailModal} backdrop={'static'} keyboard={false} onHide={() => setShowEditThumbnailModal(false)}>
          <Modal.Header>
            <Modal.Title>Conferma</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {!thumbnailUploading && (
              <>
                <LanguageCodeInput
                  value={editLanguage}
                  onChange={(e) => setEditLanguage(e)}
                  disabled={!editLanguageEnabled}
                  onErrorChange={(e) => setIsLanguageCodeValid(!e)}
                  autoFocus={editLanguageEnabled}
                />

                <Form.Group>
                  <Form.Label>
                    Thumbnail * <small className={'text-muted'}>(WEBP)</small>
                  </Form.Label>
                  <Form.File accept={'image/webp'} onChange={(e) => setNewThumbnail(e.target.files[0])} />
                </Form.Group>
              </>
            )}
            {thumbnailUploading && (
              <ProgressBar
                variant={thumbnailUploadProgress < 100 ? 'primary' : 'success'}
                animated={thumbnailUploadProgress < 100}
                now={thumbnailUploadProgress}
                label={`${thumbnailUploadProgress}%`}
              />
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant={'secondary'} disabled={thumbnailUploading} onClick={() => setShowEditThumbnailModal(false)}>
              Annulla
            </Button>
            <Button
              variant={'primary'}
              disabled={!newThumbnail || thumbnailUploading || !isLanguageCodeValid}
              onClick={() => {
                setThumbnailUploading(true);
                handleUploadTask(video.id, newThumbnail, `thumbnail_${editLanguage}.webp`, setThumbnailUploadProgress)
                  .then(() => {
                    setShowEditThumbnailModal(false);
                    onUpdate();
                  })
                  .catch(console.error)
                  .finally(() => {
                    setThumbnailUploading(false);
                  });
              }}
            >
              Conferma
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      {showEditThumbnailVerticalModal && (
        <Modal show={showEditThumbnailVerticalModal} backdrop={'static'} keyboard={false} onHide={() => setShowEditThumbnailVerticalModal(false)}>
          <Modal.Header>
            <Modal.Title>Modifica thumbnail verticale</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {!thumbnailVerticalUploading && (
              <>
                <LanguageCodeInput
                  value={editLanguage}
                  onChange={(e) => setEditLanguage(e)}
                  disabled={!editLanguageEnabled}
                  onErrorChange={(e) => setIsLanguageCodeValid(!e)}
                  autoFocus={editLanguageEnabled}
                />

                <Form.Group>
                  <Form.Label>
                    Thumbnail verticale * <small className={'text-muted'}>(WEBP)</small>
                  </Form.Label>
                  <Form.File accept={'image/webp'} onChange={(e) => setNewThumbnailVertical(e.target.files[0])} />
                </Form.Group>
              </>
            )}
            {thumbnailVerticalUploading && (
              <ProgressBar
                variant={thumbnailVerticalUploadProgress < 100 ? 'primary' : 'success'}
                animated={thumbnailVerticalUploadProgress < 100}
                now={thumbnailVerticalUploadProgress}
                label={`${thumbnailVerticalUploadProgress}%`}
              />
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant={'secondary'} disabled={thumbnailVerticalUploading} onClick={() => setShowEditThumbnailVerticalModal(false)}>
              Annulla
            </Button>
            <Button
              variant={'primary'}
              disabled={!newThumbnailVertical || thumbnailVerticalUploading || !isLanguageCodeValid}
              onClick={() => {
                setThumbnailVerticalUploading(true);
                handleUploadTask(video.id, newThumbnailVertical, `thumbnail_vertical_${editLanguage}.webp`, setThumbnailVerticalUploadProgress)
                  .then(() => {
                    setShowEditThumbnailVerticalModal(false);
                    onUpdate();
                  })
                  .catch(console.error)
                  .finally(() => {
                    setThumbnailVerticalUploading(false);
                  });
              }}
            >
              Conferma
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      {showEditTrailerModal && (
        <Modal show={showEditTrailerModal} backdrop={'static'} keyboard={false} onHide={() => setShowEditTrailerModal(false)}>
          <Modal.Header>
            <Modal.Title>Modifica trailer</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {!trailerUploading && (
              <>
                <LanguageCodeInput
                  value={editLanguage}
                  onChange={(e) => setEditLanguage(e)}
                  disabled={!editLanguageEnabled}
                  onErrorChange={(e) => setIsLanguageCodeValid(!e)}
                  autoFocus={editLanguageEnabled}
                />

                <Form.Group>
                  <Form.Label>
                    Trailer * <small className={'text-muted'}>(WEBM)</small>
                  </Form.Label>
                  <Form.File accept={'video/webm'} onChange={(e) => setNewTrailer(e.target.files[0])} />
                </Form.Group>
              </>
            )}
            {trailerUploading && (
              <ProgressBar
                variant={trailerUploadProgress < 100 ? 'primary' : 'success'}
                animated={trailerUploadProgress < 100}
                now={trailerUploadProgress}
                label={`${trailerUploadProgress}%`}
              />
            )}
          </Modal.Body>
          <Modal.Footer>
            {/*<Button
            variant={'outline-danger'}
            disabled={!video.trailer || trailerUploading}
            className={'mr-auto'}
            onClick={() => {
              setTrailerUploading(true);
              storage
                .child(`video_data/${video.id}/trailer.webm`)
                .delete()
                .then(() => {
                  setShowEditTrailerModal(false);
                  onUpdate();
                })
                .catch(console.error)
                .finally(() => {
                  setTrailerUploading(false);
                });
            }}
          >
            Rimuovi trailer
          </Button>*/}
            <Button variant={'secondary'} disabled={trailerUploading} onClick={() => setShowEditTrailerModal(false)}>
              Annulla
            </Button>
            <Button
              variant={'primary'}
              disabled={!newTrailer || trailerUploading || !isLanguageCodeValid}
              onClick={() => {
                setTrailerUploading(true);
                handleUploadTask(video.id, newTrailer, `trailer_${editLanguage}.webm`, setTrailerUploadProgress)
                  .then(() => {
                    setShowEditTrailerModal(false);
                    onUpdate();
                  })
                  .catch(console.error)
                  .finally(() => {
                    setTrailerUploading(false);
                  });
              }}
            >
              Conferma
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      {showEditAuthorModal && (
        <Modal show={showEditAuthorModal} backdrop={'static'} keyboard={false} onHide={() => setShowEditAuthorModal(false)}>
          <Modal.Header>
            <Modal.Title>Modifica autore</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Control as={'select'} disabled={newAuthorLoading} value={newAuthor} onChange={(e) => setNewAuthor(e.target.value)}>
              <option value={''} disabled>
                Seleziona...
              </option>
              {authors.map((author) => (
                <option key={author.id} value={author.id} disabled={author.id == video.author?.id}>
                  {author.name}
                </option>
              ))}
            </Form.Control>
          </Modal.Body>
          <Modal.Footer>
            <Button variant={'secondary'} disabled={newAuthorLoading} onClick={() => setShowEditAuthorModal(false)}>
              Annulla
            </Button>
            <Button
              variant={'primary'}
              disabled={!newAuthor || newAuthorLoading}
              onClick={() => {
                setNewAuthorLoading(true);
                Promise.all([
                  database.doc(`videos/${video.id}`).set({ author: database.doc(`authors/${newAuthor}`), ...defaultUpdateValues }, { merge: true }),
                  startSeoUpdate(),
                ])
                  .then(() => {
                    setShowEditAuthorModal(false);
                    onUpdate();
                  })
                  .catch(console.error)
                  .finally(() => setNewAuthorLoading(false));
              }}
            >
              Conferma
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      {showEditDescriptionModal && (
        <Modal show={showEditDescriptionModal} size={'xl'} backdrop={'static'} keyboard={false} onHide={() => setShowEditDescriptionModal(false)} scrollable>
          <Modal.Header>
            <Modal.Title>Modifica descrizione</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <LanguageCodeInput
              value={editLanguage}
              onChange={(e) => setEditLanguage(e)}
              disabled={!editLanguageEnabled}
              onErrorChange={(e) => setIsLanguageCodeValid(!e)}
              autoFocus={editLanguageEnabled}
            />

            {newDescription.map((segment, i) => (
              <div key={i.toString()} className={'mb-2'}>
                <InputGroup>
                  <Form.Control
                    as={'textarea'}
                    disabled={newDescriptionLoading}
                    value={segment}
                    onChange={(e) => {
                      newDescription[i] = e.target.value;
                      detectChanges();
                    }}
                  />
                  <InputGroup.Append>
                    <Button
                      disabled={newDescriptionLoading}
                      variant={'danger'}
                      onClick={() => {
                        newDescription.splice(i, 1);
                        detectChanges();
                      }}
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </Button>
                  </InputGroup.Append>
                </InputGroup>
              </div>
            ))}
            <Button
              variant={'primary'}
              size={'sm'}
              disabled={newDescriptionLoading}
              onClick={() => {
                newDescription.push('');
                detectChanges();
              }}
            >
              <Plus /> Aggiungi paragrafo
            </Button>
          </Modal.Body>
          <Modal.Footer>
            <Button variant={'secondary'} disabled={newDescriptionLoading} onClick={() => setShowEditDescriptionModal(false)}>
              Annulla
            </Button>
            <Button
              variant={'primary'}
              disabled={newDescriptionLoading || !isLanguageCodeValid}
              onClick={() => {
                setNewDescriptionLoading(true);
                const description = newDescription.map((row) => row?.trim?.()).filter(Boolean);
                Promise.all([
                  database.doc(`videos/${video.id}`).set(
                    {
                      i18n: { [editLanguage]: { description } },
                      ...defaultUpdateValues,
                    },
                    { merge: true }
                  ),
                  startSeoUpdate(),
                ])
                  .then(() => {
                    setShowEditDescriptionModal(false);
                    onUpdate();
                  })
                  .catch(console.error)
                  .finally(() => setNewDescriptionLoading(false));
              }}
            >
              Conferma
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      {showEditInfoModal && (
        <Modal show={showEditInfoModal} size={'xl'} backdrop={'static'} keyboard={false} onHide={() => setShowEditInfoModal(false)} scrollable>
          <Modal.Header>
            <Modal.Title>Modifica informazioni</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <LanguageCodeInput
              value={editLanguage}
              onChange={(e) => setEditLanguage(e)}
              disabled={!editLanguageEnabled}
              onErrorChange={(e) => setIsLanguageCodeValid(!e)}
              autoFocus={editLanguageEnabled}
            />

            <div className={'d-flex'}>
              <Form.Group className={'mb-0 w-50'}>
                <InputGroup>
                  <Form.Control
                    as={'textarea'}
                    className={'small'}
                    style={{ fontFamily: "'Nanum Gothic Coding', monospace" }}
                    disabled={newInfoLoading}
                    value={newInfo}
                    onChange={(e) => {
                      setNewInfo(e.target.value);
                    }}
                  />
                </InputGroup>
                <Form.Text>
                  In questo campo è possibile inserire codice HTML. Tag supportati: <br />{' '}
                  {BASIC_FORMATTING_ALLOWED_TAGS.map((e) => (
                    <code key={e} className={'mr-1'}>
                      {e}
                    </code>
                  ))}
                </Form.Text>
              </Form.Group>
              <div
                className={`w-50 border rounded ml-2 py-1 px-2 small ${newInfo ? '' : 'text-muted'}`}
                {...innerHtmlBasicFormatting(newInfo || 'Anteprima contenuto...')}
              />
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant={'secondary'} disabled={newInfoLoading} onClick={() => setShowEditInfoModal(false)}>
              Annulla
            </Button>
            <Button
              variant={'primary'}
              disabled={newInfoLoading || !isLanguageCodeValid}
              onClick={() => {
                const info = innerHtmlBasicFormatting(newInfo).dangerouslySetInnerHTML.__html.trim();
                if (!info) return;
                setNewInfoLoading(true);
                Promise.all([
                  database.doc(`videos/${video.id}`).set(
                    {
                      i18n: { [editLanguage]: { info } },
                      ...defaultUpdateValues,
                    },
                    { merge: true }
                  ),
                  startSeoUpdate(),
                ])
                  .then(() => {
                    setShowEditInfoModal(false);
                    onUpdate();
                  })
                  .catch(console.error)
                  .finally(() => setNewInfoLoading(false));
              }}
            >
              Conferma
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      {showEditTagsModal && (
        <Modal show={showEditTagsModal} backdrop={'static'} keyboard={false} onHide={() => setShowEditTagsModal(false)}>
          <Modal.Header>
            <Modal.Title>Modifica tags</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className={'mb-0'}>
              <Form.Control
                as={'select'}
                multiple
                disabled={newTagsLoading}
                value={newTags}
                style={{ height: '400px' }}
                onChange={(e: ChangeEvent<HTMLSelectElement>) => setNewTags(Array.from(e.target.selectedOptions).map((opt) => opt.value))}
              >
                {tags.map((tag) => (
                  <option key={tag.id} value={tag.id}>
                    {tag.label} {tag.showAsSeries && '[CATEGORIA]'}
                  </option>
                ))}
              </Form.Control>
              <Form.Text className={'d-flex justify-content-between align-items-baseline'}>
                <span className={'text-muted'}>(CTRL+Click per selezione multipla o per de-selezionare)</span>
                <Button size={'sm'} variant={'outline-danger'} onClick={() => setNewTags([])}>
                  Deseleziona tutti
                </Button>
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant={'secondary'} disabled={newTagsLoading} onClick={() => setShowEditTagsModal(false)}>
              Annulla
            </Button>
            <Button
              variant={'primary'}
              disabled={newTagsLoading}
              onClick={() => {
                setNewTagsLoading(true);
                const tags = newTags.map((tagId) => database.doc(`tags/${tagId}`));
                Promise.all([database.doc(`videos/${video.id}`).set({ tags, ...defaultUpdateValues }, { merge: true }), startSeoUpdate()])
                  .then(() => {
                    setShowEditTagsModal(false);
                    onUpdate();
                  })
                  .catch(console.error)
                  .finally(() => setNewTagsLoading(false));
              }}
            >
              Conferma
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      {showEditKeywordsModal && (
        <Modal size={'lg'} show={showEditKeywordsModal} backdrop={'static'} keyboard={false} onHide={() => setShowEditKeywordsModal(false)}>
          <Modal.Header>
            <Modal.Title>Modifica keywords</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <LanguageCodeInput
              value={editLanguage}
              onChange={(e) => setEditLanguage(e)}
              disabled={!editLanguageEnabled || newKeywordsLoading}
              onErrorChange={(e) => setIsLanguageCodeValid(!e)}
              autoFocus={editLanguageEnabled}
            />

            <Form.Group>
              <Form.Label>Keywords</Form.Label>
              <Form.Control
                autoFocus
                as={'textarea'}
                rows={6}
                disabled={newKeywordsLoading}
                value={newKeywords}
                onChange={(e) => setNewKeywords(e.target.value)}
                placeholder={'Inserisci una lista di parole chiave separate da virgola...'}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant={'secondary'} disabled={newKeywordsLoading} onClick={() => setShowEditKeywordsModal(false)}>
              Annulla
            </Button>
            <Button
              variant={'primary'}
              disabled={!newKeywords || newKeywordsLoading || !isLanguageCodeValid}
              onClick={() => {
                setNewKeywordsLoading(true);
                Promise.all([
                  database.doc(`videos/${video.id}`).set(
                    {
                      [`keywords_${editLanguage}`]: newKeywords,
                      ...defaultUpdateValues,
                    },
                    { merge: true }
                  ),
                  startSeoUpdate(),
                ])
                  .then(() => {
                    setShowEditKeywordsModal(false);
                    onUpdate();
                  })
                  .catch(console.error)
                  .finally(() => setNewKeywordsLoading(false));
              }}
            >
              Conferma
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      {showEditTimestampsModal && (
        <Modal show={showEditTimestampsModal} size={'xl'} backdrop={'static'} keyboard={false} onHide={() => setShowEditTimestampsModal(false)} scrollable>
          <Modal.Header>
            <Modal.Title>Modifica timestamps</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className={'mb-1'}>Aggiungi lingua a tutti gli elementi</div>
            <div>
              <LanguageCodeInput
                inline={true}
                onAdd={(lang) => {
                  newTimestamps.forEach((ts) => {
                    if (!ts.i18n[lang]) {
                      ts.i18n[lang] = { description: '' };
                      detectChanges();
                    }
                  });
                  return true;
                }}
              />
            </div>
            <div className={'mt-1 mb-3 small'}>La lingua verrà aggiunta solo agli elementi che già non la contengono</div>

            {newTimestamps.map((ts, i) => (
              <div key={i.toString()} className={'d-flex mb-2'}>
                <div style={{ width: '20%' }} className={'mr-2'}>
                  <InputGroup size={'sm'}>
                    <InputGroup.Prepend>
                      <Button variant={'light'} style={{ pointerEvents: 'none', color: 'white' }}>
                        <FontAwesomeIcon icon={faStopwatch} />
                      </Button>
                    </InputGroup.Prepend>
                    <Form.Control
                      disabled={newTimestampsLoading}
                      placeholder={'Secondi *'}
                      isInvalid={!ts.at}
                      isValid={!!ts.at}
                      value={ts.at}
                      onChange={(e) => {
                        ts.at = e.target.value;
                        detectChanges();
                      }}
                    />
                  </InputGroup>
                  <Button
                    block
                    size={'sm'}
                    className={'mt-1'}
                    disabled={newTimestampsLoading}
                    variant={'outline-danger'}
                    onClick={() => {
                      newTimestamps.splice(i, 1);
                      detectChanges();
                    }}
                  >
                    <FontAwesomeIcon icon={faTimes} fixedWidth={true} />
                    <span className={'ml-1'}>Elimina blocco</span>
                  </Button>
                </div>
                <div style={{ width: '80%' }} className={'d-flex flex-column'}>
                  {Object.entries<any>(ts.i18n || {}).map((entry) => {
                    const [lang, { description }] = entry;
                    return (
                      <div key={lang} className={'d-flex align-items-center mb-1'}>
                        <InputGroup size={'sm'}>
                          <InputGroup.Prepend>
                            <Button variant={'light'} style={{ pointerEvents: 'none' }}>
                              <CountryFlag language={lang} />
                            </Button>
                          </InputGroup.Prepend>
                          <Form.Control
                            disabled={newTimestampsLoading}
                            placeholder={`Descrizione *`}
                            isInvalid={!description}
                            isValid={!!description}
                            value={description}
                            onChange={(e) => {
                              ts.i18n[lang].description = e.target.value;
                              detectChanges();
                            }}
                          />
                          <InputGroup.Append>
                            <Button
                              disabled={newTimestampsLoading}
                              variant={'danger'}
                              onClick={() => {
                                delete ts.i18n[lang];
                                detectChanges();
                              }}
                            >
                              <FontAwesomeIcon icon={faTimes} fixedWidth={true} />
                            </Button>
                          </InputGroup.Append>
                        </InputGroup>
                      </div>
                    );
                  })}
                  <div className={'d-flex align-items-center'}>
                    <LanguageCodeInput
                      size={'sm'}
                      inline={true}
                      onAdd={(lang) => {
                        if (!ts.i18n[lang]) {
                          ts.i18n[lang] = { description: '' };
                          detectChanges();
                          return true;
                        }
                      }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </Modal.Body>
          <Modal.Footer>
            <Button
              disabled={newTimestampsLoading}
              variant={'primary'}
              onClick={() => {
                newTimestamps.push({ at: '', description: '', i18n: {} });
                detectChanges();
              }}
            >
              <Plus /> Aggiungi timestamp
            </Button>

            <div className={'mr-auto'}>
              {timestampsLanguageMap.map(([l, x]) => (
                <span className={'border rounded d-inline-flex align-items-center mr-1 px-1'} key={l}>
                  <CountryFlag language={l} withTooltip={true} />
                  <span className={'ml-1'}>{x}</span>
                </span>
              ))}
              {timestampsLanguageMapDiscrepancy && (
                <span className={'border rounded border-danger d-inline-flex align-items-center mr-1 px-1 text-danger'}>
                  <FontAwesomeIcon icon={faExclamationTriangle} size={'xs'} />
                  <span className={'ml-1 font-weight-bold small'}>Discrepanza</span>
                </span>
              )}
            </div>

            <Button variant={'secondary'} disabled={newTimestampsLoading} onClick={() => setShowEditTimestampsModal(false)}>
              Annulla
            </Button>
            <Button
              variant={'primary'}
              disabled={newTimestampsLoading || !newTimestamps.every((ts) => ts.at && ts.i18n)}
              onClick={() => {
                setNewTimestampsLoading(true);
                Promise.all([database.doc(`videos/${video.id}`).set({ ts: newTimestamps, ...defaultUpdateValues }, { merge: true }), startSeoUpdate()])
                  .then(() => {
                    setShowEditTimestampsModal(false);
                    onUpdate();
                  })
                  .catch(console.error)
                  .finally(() => setNewTimestampsLoading(false));
              }}
            >
              Conferma
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      {showEditXRayModal && (
        <Modal show={showEditXRayModal} size={'xl'} backdrop={'static'} keyboard={false} onHide={() => setShowEditXRayModal(false)} scrollable>
          <Modal.Header>
            <Modal.Title>Modifica strumentazione</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Alert variant={'danger'}>
              <p>
                <strong>Attenzione!</strong>
              </p>
              <p>Per ogni elemento (se già salvato su database) è presente una variante della descrizione utilizzata per generare le statistiche di visione.</p>
              <p>
                Se si modifica la descrizione di un elemento, questa verrà rigenerata ma il nuovo identificativo si integrerà con il precedente, ovvero le
                statistiche della nuova descrizione si aggregheranno alla precedente descrizione.
              </p>
              <p className={'mb-0'}>
                In breve, <strong>modificare la descrizione solo per correzioni ortografiche</strong>, se si desidera sostituire completamente l'elemento{' '}
                <strong>è necessario crearne uno nuovo</strong>.
              </p>
            </Alert>

            <div className={'mb-1'}>Aggiungi lingua a tutti gli elementi</div>
            <div>
              <LanguageCodeInput
                inline={true}
                onAdd={(lang) => {
                  newXRay.forEach((xray) => {
                    if (!xray.i18n[lang]) {
                      xray.i18n[lang] = { description: '' };
                      detectChanges();
                    }
                  });
                  return true;
                }}
              />
            </div>
            <div className={'mt-1 mb-3 small'}>La lingua verrà aggiunta solo agli elementi che già non la contengono</div>

            {newXRay.map((xray, i) => (
              <div key={i.toString()} className={'d-flex mb-2'}>
                <div style={{ width: '20%' }} className={'mr-2'}>
                  <InputGroup size={'sm'}>
                    <InputGroup.Prepend>
                      <Button variant={'light'} style={{ pointerEvents: 'none', color: 'white' }}>
                        <FontAwesomeIcon icon={faHourglassStart} />
                      </Button>
                    </InputGroup.Prepend>
                    <Form.Control
                      disabled={newXRayLoading}
                      placeholder={'Da *'}
                      isInvalid={!xray.from}
                      isValid={!!xray.from}
                      value={xray.from}
                      onChange={(e) => {
                        xray.from = e.target.value;
                        detectChanges();
                      }}
                    />
                  </InputGroup>
                  <InputGroup className={'mt-1'} size={'sm'}>
                    <InputGroup.Prepend>
                      <Button variant={'light'} style={{ pointerEvents: 'none', color: 'white' }}>
                        <FontAwesomeIcon icon={faHourglassEnd} />
                      </Button>
                    </InputGroup.Prepend>
                    <Form.Control
                      disabled={newXRayLoading}
                      placeholder={'A *'}
                      isInvalid={!xray.to}
                      isValid={!!xray.to}
                      value={xray.to}
                      onChange={(e) => {
                        xray.to = e.target.value;
                        detectChanges();
                      }}
                    />
                  </InputGroup>
                  <Button
                    block
                    size={'sm'}
                    className={'mt-1'}
                    disabled={newXRayLoading}
                    variant={'outline-danger'}
                    onClick={() => {
                      newXRay.splice(i, 1);
                      detectChanges();
                    }}
                  >
                    <FontAwesomeIcon icon={faTimes} fixedWidth={true} />
                    <span className={'ml-1'}>Elimina blocco</span>
                  </Button>
                </div>
                <div style={{ width: '80%' }} className={'d-flex flex-column'}>
                  {!!xray.cleanDescription && (
                    <InputGroup size={'sm'} className={'mb-1'}>
                      <InputGroup.Prepend>
                        <Button variant={'light'} style={{ pointerEvents: 'none', padding: '0 10px', color: 'white' }}>
                          <FontAwesomeIcon icon={faLock} />
                        </Button>
                      </InputGroup.Prepend>
                      <Form.Control value={xray.cleanDescription} disabled={true} />
                    </InputGroup>
                  )}
                  {Object.entries<any>(xray.i18n || {}).map((entry) => {
                    const [lang, { description }] = entry;
                    return (
                      <div key={lang} className={'d-flex align-items-center mb-1'}>
                        <InputGroup size={'sm'}>
                          <InputGroup.Prepend>
                            <Button variant={'light'} style={{ pointerEvents: 'none' }}>
                              <CountryFlag language={lang} />
                            </Button>
                          </InputGroup.Prepend>
                          <Form.Control
                            disabled={newTimestampsLoading}
                            placeholder={`Descrizione *`}
                            isInvalid={!description}
                            isValid={!!description}
                            value={description}
                            onChange={(e) => {
                              xray.i18n[lang].description = e.target.value;
                              detectChanges();
                            }}
                          />
                          <InputGroup.Append>
                            <Button
                              disabled={newTimestampsLoading}
                              variant={'danger'}
                              onClick={() => {
                                delete xray.i18n[lang];
                                detectChanges();
                              }}
                            >
                              <FontAwesomeIcon icon={faTimes} fixedWidth={true} />
                            </Button>
                          </InputGroup.Append>
                        </InputGroup>
                      </div>
                    );
                  })}
                  <div className={'d-flex align-items-center'}>
                    <LanguageCodeInput
                      size={'sm'}
                      inline={true}
                      onAdd={(lang) => {
                        if (!xray.i18n[lang]) {
                          xray.i18n[lang] = { description: '' };
                          detectChanges();
                          return true;
                        }
                      }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </Modal.Body>
          <Modal.Footer>
            <Button
              disabled={newXRayLoading}
              variant={'primary'}
              onClick={() => {
                newXRay.push({ from: '', to: '', description: '', i18n: {} });
                detectChanges();
              }}
            >
              <Plus /> Aggiungi strumentazione
            </Button>

            <div className={'mr-auto'}>
              {xrayLanguageMap.map(([l, x]) => (
                <span className={'border rounded d-inline-flex align-items-center mr-1 px-1'} key={l}>
                  <CountryFlag language={l} withTooltip={true} />
                  <span className={'ml-1'}>{x}</span>
                </span>
              ))}
              {xrayLanguageMapDiscrepancy && (
                <span className={'border rounded border-danger d-inline-flex align-items-center mr-1 px-1 text-danger'}>
                  <FontAwesomeIcon icon={faExclamationTriangle} size={'xs'} />
                  <span className={'ml-1 font-weight-bold small'}>Discrepanza</span>
                </span>
              )}
            </div>

            <Button variant={'secondary'} disabled={newXRayLoading} onClick={() => setShowEditXRayModal(false)}>
              Annulla
            </Button>
            <Button
              variant={'primary'}
              disabled={newXRayLoading || !newXRay.every((xray) => xray.from && xray.to && xray.i18n)}
              onClick={() => {
                setNewXRayLoading(true);
                Promise.all([database.doc(`videos/${video.id}`).set({ xray: newXRay, ...defaultUpdateValues }, { merge: true }), startSeoUpdate()])
                  .then(() => {
                    setShowEditXRayModal(false);
                    onUpdate();
                  })
                  .catch(console.error)
                  .finally(() => setNewXRayLoading(false));
              }}
            >
              Conferma
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      {showRawDataModal && (
        <Modal className={'dark-modal'} show={showRawDataModal} onHide={() => setShowRawDataModal(false)} size={'xl'} centered animation={false}>
          <Modal.Header closeButton>
            <Modal.Title>Raw data per video {video.id}</Modal.Title>
          </Modal.Header>
          <JSONInput
            locale={locale}
            style={{ outerBox: { width: '100%' }, container: { width: '100%' } }}
            confirmGood={false}
            placeholder={video}
            viewOnly={true}
          />
        </Modal>
      )}

      {showLogsModal && (
        <Modal
          className={'dark-modal'}
          show={showLogsModal}
          onHide={() => setShowLogsModal(false)}
          size={'xl'}
          centered
          scrollable
          backdrop={'static'}
          keyboard={false}
        >
          <Modal.Header closeButton>
            <Modal.Title>Logs per video {video.id}</Modal.Title>
          </Modal.Header>
          <Modal.Body className={'p-0'}>
            <Table striped bordered variant={'dark'} className={'mb-0 content-logs-table'}>
              {!logsLoading && (
                <thead>
                  <tr>
                    <th>
                      <div className={'d-flex justify-content-between align-items-center'}>
                        Timestamp <FontAwesomeIcon className={'text-muted'} icon={faArrowDown} />
                      </div>
                    </th>
                    <th>Author</th>
                    <th style={{ width: '1px', whiteSpace: 'nowrap' }} className={'text-center'}>
                      Data
                    </th>
                  </tr>
                </thead>
              )}
              <tbody>
                {logsLoading && (
                  <tr>
                    <td colSpan={999}>
                      <FullscreenLoader fullscreen={false} />
                    </td>
                  </tr>
                )}
                {!logsLoading && !logs.length && (
                  <tr>
                    <td colSpan={999} className={'text-center'}>
                      Nessun log da visualizzare
                    </td>
                  </tr>
                )}
                {!logsLoading &&
                  !!logs.length &&
                  logs.map((log) => (
                    <Fragment key={log.id}>
                      <tr className={selectedLog == log.id ? 'bg-primary text-dark' : ''}>
                        <td style={{ verticalAlign: 'middle' }}>{formatSecondsTimestamp(log.timestamp._seconds, 'dd/MM/y HH:mm')}</td>
                        <td style={{ verticalAlign: 'middle' }}>{!!log.by ? <UserUID uid={log.by} /> : <Badge variant={'warning'}>SYSTEM</Badge>}</td>
                        <td style={{ verticalAlign: 'middle' }}>
                          {!!log.data && (
                            <IconButton
                              icon={faCode}
                              onClick={() => {
                                if (selectedLog == log.id) {
                                  setSelectedLog(null);
                                  return;
                                }

                                if (log.data.before || log.data.after) {
                                  setLogDetailsBefore(stringify(log.data.before || {}));
                                  setLogDetailsAfter(stringify(log.data.after || {}));
                                } else {
                                  if (log.event == 'DELETE') setLogDetailsBefore(stringify(log.data));
                                  else setLogDetailsAfter(stringify(log.data));
                                }
                                setSelectedLog(log.id);
                              }}
                            />
                          )}
                        </td>
                      </tr>
                      {selectedLog == log.id && (
                        <tr>
                          <td colSpan={999} className={'p-0 table-resetter'}>
                            {/*TODO: fix css perché react diff viewer usa una table, ma noi essendo dentro una table ereditiamo gli stili*/}
                            <ReactDiffViewer
                              oldValue={logDetailsBefore}
                              newValue={logDetailsAfter}
                              splitView={true}
                              leftTitle={'Before'}
                              rightTitle={'After'}
                              compareMethod={DiffMethod.WORDS}
                            />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
              </tbody>
              {!logsLoading && (
                <tfoot>
                  <tr>
                    <td colSpan={999}>
                      <div className={'d-flex justify-content-between align-items-center'}>
                        <span>Pagina {currentPage + 1}</span>
                        <div className={'d-flex'}>
                          {!!currentPage && (
                            <IconButton
                              icon={faChevronLeft}
                              onClick={() => {
                                setCurrentPage((current) => current - 1);
                              }}
                            />
                          )}
                          {!!logs.length && (
                            <IconButton
                              icon={faChevronRight}
                              onClick={() => {
                                setCurrentPage((current) => current + 1);
                              }}
                            />
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                </tfoot>
              )}
            </Table>
          </Modal.Body>
        </Modal>
      )}

      {genericUploadModal && (
        <Modal show={genericUploadModal} onHide={() => setGenericUploadModal(false)} centered backdrop={'static'} keyboard={false}>
          <Modal.Header>
            <Modal.Title>Upload in corso</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div>Attendere&hellip;</div>
            <ProgressBar
              variant={genericUploadProgress < 100 ? 'primary' : 'success'}
              animated={genericUploadProgress < 100}
              now={genericUploadProgress}
              label={`${genericUploadProgress}%`}
            />
          </Modal.Body>
        </Modal>
      )}

      {subtitleModalOpen && (
        <SubtitleUploader
          show={subtitleModalOpen}
          onHide={() => setSubtitleModalOpen(false)}
          onConfirm={async ({ file }) => {
            setGenericUploadModal(true);
            await handleUploadTask(video.id, file, `subs/${file.name}`, setGenericUploadProgress);
            onUpdate();
          }}
        />
      )}

      {showRescheduleModal && selectedNotification && (
        <Modal
          show={showRescheduleModal}
          onHide={() => {
            setNewScheduleLoading(false);
            setSelectedNotification(null);
            setScheduledDate('');
            setScheduledTime('');
          }}
          centered
          backdrop={'static'}
          keyboard={false}
        >
          <Modal.Header>
            <Modal.Title>{!!selectedNotification.scheduled! ? 'Riprogramma' : 'Programma'} data invio notifica</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group>
              <Form.Label>Data e ora invio notifica</Form.Label>
              <InputGroup>
                <Form.Control
                  type={'date'}
                  disabled={newScheduleLoading}
                  style={{ maxWidth: '155px' }}
                  value={scheduledDate}
                  onChange={(e) => setScheduledDate(e.target.value)}
                />
                <Form.Control
                  type={'time'}
                  disabled={newScheduleLoading}
                  style={{ maxWidth: '125px' }}
                  value={scheduledTime}
                  onChange={(e) => setScheduledTime(e.target.value)}
                />
              </InputGroup>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <div className={'d-flex'}>
              <Button variant="secondary" className="mr-2" onClick={() => setShowRescheduleModal(false)}>
                Annulla
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  setNewScheduleLoading(true);
                  const newSchedule = timestamp.fromDate(new Date(`${scheduledDate}T${scheduledTime}:00`));

                  database
                    .doc(`videoNotifications/${selectedNotification.id}`)
                    .set({ scheduled: newSchedule }, { merge: true })
                    .then(() => {
                      onUpdate();
                    })
                    .catch(console.error)
                    .finally(() => {
                      setShowRescheduleModal(false);
                      setNewScheduleLoading(false);
                      setSelectedNotification(null);
                      setScheduledDate('');
                      setScheduledTime('');
                    });
                }}
              >
                Conferma
              </Button>
            </div>
          </Modal.Footer>
        </Modal>
      )}

      {showConfirmNotificationDialog && selectedNotification && (
        <Modal show={showConfirmNotificationDialog} onHide={() => setShowConfirmNotificationDialog(false)} centered backdrop={'static'} keyboard={false}>
          <Modal.Header>
            <Modal.Title>Invia notifica</Modal.Title>
          </Modal.Header>
          <Modal.Body>Desideri inviare la notifica per questo video?</Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" className="mr-2" onClick={() => setShowConfirmNotificationDialog(false)}>
              Annulla
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                sendVideoNotifications(selectedNotification.id);
              }}
            >
              Conferma
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
};

export { AdminVideoComponent as AdminVideo };
